import { memo } from "react"
import { cn } from "@/shared/utils/general/cn"
import type { DbObject } from "@/db/schema"
import { ObjectStatus, ObjectType, db } from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaskCounterProps {
  task: DbObject
  className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Unfinished-task counter badge for Project/List rows.
 *
 * v1 pattern: CardPreviewCounter.
 * Shows count of unfinished child tasks in a small bordered badge.
 * Hidden when: task is completed, or count is 0.
 */
export const TaskCounter = memo(function TaskCounter({
  task,
  className,
}: TaskCounterProps) {
  const isApplicable =
    (task.type === ObjectType.Project || task.type === ObjectType.List) &&
    task.status !== ObjectStatus.Completed

  const taskId = task.id

  // Count unfinished child tasks — hook always called (React rules)
  const [unfinishedCount] = useLiveQueryImmediate(
    () => {
      if (!isApplicable || !taskId) return 0
      return db.totodo
        .where("[parentPath+type+isDeleted]")
        .between(
          [taskId, ObjectType.Task, 0],
          [taskId, ObjectType.Task, 0],
          true,
          true,
        )
        .filter(
          (t) =>
            t.parentPath.endsWith(taskId) &&
            t.status === ObjectStatus.Unfinished,
        )
        .count()
    },
    ["task-counter", taskId, isApplicable],
    0,
  )

  if (!isApplicable || !unfinishedCount) return null

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded px-1 py-px",
        "border border-foreground/15 text-[11px] tabular-nums opacity-60",
        className,
      )}
    >
      {unfinishedCount}
    </span>
  )
})
